import classNames from 'classnames'
import { Button } from 'app/components/ui/button'
import { ReactElement, ReactNode } from 'react'

export type FilterChip<T extends string> = {
  name: string
  kind: T
  totalItems: string
  icon?: ReactNode
}

/** Kept from the hand-rolled chip so the row still fits the drawer's 668px. */
const CHIP_METRICS = `gap-x-1.5 px-2.5 text-body-xs font-normal`

/**
 * Chips toggle additively, except while "All" is on: it holds the selection
 * alone, and clicking a chip from there selects only that chip.
 */
export function FilterChips<T extends string>({
  chips,
  active,
  label,
  className,
  onToggle,
  onSelectAll,
  onSelectOnly,
}: {
  chips: FilterChip<T>[]
  active: T[]
  label: string
  className?: string
  onToggle: (kind: T) => void
  onSelectAll: () => void
  onSelectOnly: (kind: T) => void
}): ReactElement {
  const allActive = chips.every((chip) => active.includes(chip.kind))

  return (
    <div className={classNames(`flex flex-wrap items-center gap-2`, className)} aria-label={label}>
      <Button
        variant="toggle"
        size="xs"
        className={CHIP_METRICS}
        aria-pressed={allActive}
        onClick={onSelectAll}
        disabled={allActive}
      >
        All
      </Button>

      {chips.map((chip) => {
        const isActive = !allActive && active.includes(chip.kind)
        return (
          <Button
            key={chip.kind}
            variant="toggle"
            size="xs"
            className={CHIP_METRICS}
            aria-pressed={isActive}
            onClick={() => (allActive ? onSelectOnly(chip.kind) : onToggle(chip.kind))}
          >
            {chip.icon}
            <span>{chip.name}</span>
            <span className={countClass(isActive)}>{chip.totalItems}</span>
          </Button>
        )
      })}
    </div>
  )
}

/** Matches the tab badges: the count is the same object in both places. */
const countClass = (isActive: boolean) =>
  classNames(
    `flex h-5 items-center justify-center rounded border-[0.5px] px-1.5 pt-px text-label-xs leading-none`,
    isActive ? `border-blue-200 bg-blue-100 text-blue-700` : `border-default bg-gray-minimal text-gray-strong`,
  )
